import { uniq } from 'lodash-es'
import { isUserAcct, isUserId } from '#app/lib/boolean_tests'
import { newError } from '#app/lib/error'
import { logError } from '#app/lib/loggers'
import { treq } from '#app/lib/treq'
import { objectValues } from '#app/lib/utils'
import type { SocialSearchResponse } from '#server/controllers/search/lib/social_search'
import type { UserAccountUri } from '#server/types/server'
import type { User, UserId, Username } from '#server/types/user'
import { getCurrentLang } from '#user/lib/i18n'
import { loggedIn, mainUser } from '#user/lib/main_user'
import { serializeContributor, serializeUser, type SerializedContributor, type SerializedUser, type ServerUser } from './lib/users'

export type UserSearchResult = Pick<SerializedUser, '_id' | 'username' | 'bio' | 'picture' | 'isMainUser'>

export async function searchUsers (text: string) {
  // catches case with ''
  if (!text) return []
  const { results } = await treq('search', 'get', 'default', {
    types: [ 'users' ],
    lang: getCurrentLang(),
    search: text,
  }) as SocialSearchResponse
  return results.map(({ id, label, description, image }) => {
    return {
      _id: id,
      username: label,
      bio: description,
      picture: image,
    }
  }) as UserSearchResult[]
}

export type ExtendedUser = ServerUser

export async function getUsersByIds (ids: UserId[]) {
  if (ids.length === 0) return {}
  const { users } = await treq('users', 'get', 'by-ids', { ids })
  return users
}

export async function getUserById (id: UserId) {
  const users = await getUsersByIds([ id ])
  const user = users[id]
  if (!user) throw newError('user not found', 404, { userId: id })
  return user
}

export async function getSerializedUser (id: UserId) {
  const user = await getUserById(id)
  return serializeUser(user)
}

export async function getUserByUsername (username: Username) {
  const { users } = await treq('users', 'get', 'by-usernames', { usernames: [ username ] })
  return objectValues(users)[0] as User
}

type UsersByAccts = Record<UserAccountUri, SerializedContributor>

export async function getUsersByAccts (userAccts: UserAccountUri[]): Promise<UsersByAccts> {
  userAccts = uniq(userAccts)
  if (userAccts.length === 0) return {}
  const { users } = await treq('users', 'get', 'by-accts', { accts: userAccts })
  Object.values(users).forEach(serializeContributor)
  return users as UsersByAccts
}

export async function getUserByAcct (userAcct: UserAccountUri) {
  const users = await getUsersByAccts([ userAcct ])
  const user = users[userAcct]
  if (!user) throw newError('user not found', 404, { userAcct })
  return user
}

export async function resolveToUser (user: User | UserId | Username | SerializedUser) {
  let resolvedUser
  if (typeof user === 'object' && '_id' in user) {
    resolvedUser = user
  } else if (typeof user === 'string') {
    user = user.replace('acct:', '')
    if (isUserId(user)) {
      resolvedUser = await getUserById(user as UserId)
    } else if (isUserAcct(user)) {
      const contributor = await getUserByAcct(user)
      if (contributor._id) resolvedUser = await getUserById(contributor._id)
    } else {
      resolvedUser = await getUserByUsername(user)
    }
  }
  // Make sure the main user was initialized so that flags such as isMainUser can be properly set
  // Referring to mainUser in this function also makes sure the import graph gives priority to the main_user module
  if (loggedIn && !mainUser) {
    logError(new Error('resolveToUser was called before mainUser was initialized'))
  }
  if (resolvedUser) return serializeUser(resolvedUser)
  else throw newError('user not found', 404, { user })
}
